import { toast } from 'sonner';

const IMGBB_API_KEY = import.meta.env.VITE_IMGBB_API_KEY;
const IMGBB_API_URL = 'https://api.imgbb.com/1/upload';

export interface ImageUploadResponse {
  url: string;
  thumbnailUrl: string;
  deleteUrl: string;
  success: boolean;
  error?: string;
}

export interface ImageDimensions {
  width: number;
  height: number;
}

export interface ImageRequirements {
  maxSize: number;
  allowedTypes: string[];
  dimensions?: {
    minWidth: number;
    maxWidth: number;
    minHeight: number;
    maxHeight: number;
    aspectRatio?: number;
  };
  path?: string;
  allowMultiple?: boolean;
}

export interface ImageUploadResult {
  url: string;
  path: string;
  deleteUrl?: string;
  width: number;
  height: number;
  size: number;
  name: string;
}

export const DEFAULT_IMAGE_REQUIREMENTS: ImageRequirements = {
  maxSize: 5 * 1024 * 1024, // 5MB
  allowedTypes: ['image/jpeg', 'image/png', 'image/webp'],
  dimensions: {
    minWidth: 800,
    maxWidth: 4096,
    minHeight: 600,
    maxHeight: 4096
  }
};

class ImageService {
  private async getImageDimensions(file: File): Promise<ImageDimensions> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        resolve({
          width: img.width,
          height: img.height
        });
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = URL.createObjectURL(file);
    });
  }

  private async validateImage(file: File, requirements: ImageRequirements): Promise<void> {
    if (file.size > requirements.maxSize) {
      throw new Error(`File size must be less than ${requirements.maxSize / (1024 * 1024)}MB`);
    }

    if (!requirements.allowedTypes.includes(file.type)) {
      throw new Error(`File type must be one of: ${requirements.allowedTypes.join(', ')}`);
    }

    if (requirements.dimensions) {
      const dimensions = await this.getImageDimensions(file);
      const { minWidth, maxWidth, minHeight, maxHeight, aspectRatio } = requirements.dimensions;

      if (minWidth && dimensions.width < minWidth) {
        throw new Error(`Image width must be at least ${minWidth}px`);
      }
      if (maxWidth && dimensions.width > maxWidth) {
        throw new Error(`Image width must be at most ${maxWidth}px`);
      }
      if (minHeight && dimensions.height < minHeight) {
        throw new Error(`Image height must be at least ${minHeight}px`);
      }
      if (maxHeight && dimensions.height > maxHeight) {
        throw new Error(`Image height must be at most ${maxHeight}px`);
      }
      if (aspectRatio) {
        const imageAspectRatio = dimensions.width / dimensions.height;
        const tolerance = 0.1;
        if (Math.abs(imageAspectRatio - aspectRatio) > tolerance) {
          throw new Error(`Image aspect ratio must be approximately ${aspectRatio}`);
        }
      }
    }
  }

  private async compressImage(file: File, maxSizeInMB: number = 1): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }

        let { width, height } = img;
        let quality = 0.7;

        // If image is larger than 2000px in any dimension, scale it down
        const maxDimension = 2000;
        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = (height / width) * maxDimension;
            width = maxDimension;
          } else {
            width = (width / height) * maxDimension;
            height = maxDimension;
          }
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        const tryCompress = (currentQuality: number) => {
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Failed to compress image'));
                return;
              }

              if (blob.size > maxSizeInMB * 1024 * 1024 && currentQuality > 0.1) {
                // Try again with lower quality
                tryCompress(currentQuality - 0.1);
              } else {
                resolve(blob);
              }
            },
            'image/jpeg',
            currentQuality
          );
        };

        tryCompress(quality);
      };

      img.onerror = () => reject(new Error('Failed to load image for compression'));
      img.src = URL.createObjectURL(file);
    });
  }

  async uploadImage(
    file: File,
    path: string,
    allowMultiple: boolean = false
  ): Promise<ImageUploadResult> {
    try {
      if (!IMGBB_API_KEY) {
        throw new Error('ImgBB API key is not configured. Please check your environment variables.');
      }

      // Validate image against default requirements
      const requirements: ImageRequirements = {
        maxSize: DEFAULT_IMAGE_REQUIREMENTS.maxSize,
        allowedTypes: DEFAULT_IMAGE_REQUIREMENTS.allowedTypes,
        path,
        allowMultiple
      };
      
      await this.validateImage(file, requirements);

      // Compress image if it's larger than 1MB
      const imageToUpload = file.size > 1024 * 1024 
        ? await this.compressImage(file)
        : file;

      const formData = new FormData();
      formData.append('key', IMGBB_API_KEY);
      formData.append('image', await this.fileToBase64(imageToUpload));

      // Add folder organization
      const timestamp = new Date().toISOString().split('T')[0];
      const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.]/g, '_');
      const fullPath = `${path}/${timestamp}/${sanitizedFileName}`;
      formData.append('name', fullPath);

      const response = await fetch(IMGBB_API_URL, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          errorData?.error?.message || 
          `Upload failed with status ${response.status}: ${response.statusText}`
        );
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error?.message || 'Failed to upload image');
      }

      return {
        url: data.data.url,
        path: `${path}/${file.name}`,
        deleteUrl: data.data.delete_url,
        width: data.data.width,
        height: data.data.height,
        size: data.data.size,
        name: data.data.title,
      };
    } catch (error) {
      console.error('Image upload error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to upload image';
      toast.error(errorMessage);
      throw error;
    }
  }

  private async fileToBase64(file: File | Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64String = reader.result as string;
        // Remove the data URL prefix (e.g., "data:image/jpeg;base64,")
        const base64 = base64String.split(',')[1];
        resolve(base64);
      };
      reader.onerror = (error) => reject(error);
    });
  }

  async deleteImage(deleteUrl: string): Promise<boolean> {
    try {
      if (!deleteUrl) return false;

      const response = await fetch(deleteUrl);
      if (!response.ok) {
        throw new Error('Failed to delete image');
      }

      const data = await response.json();
      return data.success || false;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete image';
      toast.error(errorMessage);
      return false;
    }
  }
}

export const imageService = new ImageService();

// Export helper functions
export const uploadImage = async (
  file: File,
  path: string,
  allowMultiple: boolean = false
): Promise<ImageUploadResult> => {
  return imageService.uploadImage(file, path, allowMultiple);
};

export const deleteImage = (deleteUrl: string) => imageService.deleteImage(deleteUrl); 